import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import {
  Favorite as FavoriteIcon,
  Google as GoogleIcon,
  CheckCircle as CheckCircleIcon,
  LinkOff as LinkOffIcon,
} from "@mui/icons-material";
import { GoogleRevokeDialog } from "@/pages/fluxextensions/components/GoogleRevokeDialog";
import { useGoogleHealthcareAuth } from "@/hooks/useGoogleHealthcareAuth";

interface GoogleHealthcareAuthCardProps {
  compact?: boolean;
  showRevokeDialog?: boolean;
  /** Exibe botões de desvincular (desligado no modal fitness — use o Perfil). */
  showDisconnect?: boolean;
}

export function GoogleHealthcareAuthCard({
  compact,
  showRevokeDialog = true,
  showDisconnect = true,
}: GoogleHealthcareAuthCardProps) {
  const { isConnected, isLoading, handleLogin, handleLogout } =
    useGoogleHealthcareAuth();
  const [openRevokeModal, setOpenRevokeModal] = useState(false);

  const handleDisconnect = async () => {
    await handleLogout();
    if (showRevokeDialog) {
      setOpenRevokeModal(true);
    }
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: compact ? 2 : { xs: 2, sm: 3 },
          border: "1px solid",
          borderColor: isConnected ? alpha("#4CAF50", 0.3) : "grey.200",
          borderRadius: 2,
          background: isConnected
            ? `linear-gradient(135deg, ${alpha("#4CAF50", 0.05)} 0%, ${alpha(
                "#2196F3",
                0.05
              )} 100%)`
            : "transparent",
        }}
      >
        <Stack spacing={compact ? 1 : 2.5}>
          {/* No modo compact (ex.: modal fitness), em mobile empilha status e botão em coluna. */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={compact ? { xs: "flex-start", sm: "center" } : { xs: "flex-start", sm: "center" }}
            spacing={2}
          >
            <Box
              sx={{
                width: compact ? 44 : 56,
                height: compact ? 44 : 56,
                flexShrink: 0,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: isConnected
                  ? "linear-gradient(135deg, #4285F4 0%, #34A853 50%, #FBBC05 75%, #EA4335 100%)"
                  : "linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)",
              }}
            >
              <GoogleIcon
                sx={{
                  fontSize: compact ? 22 : 28,
                  color: isConnected ? "white" : "grey.500",
                }}
              />
            </Box>
            <Stack
              direction={
                compact ? { xs: "column", sm: "row" } : "row"
              }
              alignItems={
                compact ? { xs: "stretch", sm: "center" } : "center"
              }
              justifyContent="space-between"
              sx={{ flex: 1, gap: 2, minWidth: 0 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  flexWrap="wrap"
                  sx={{ gap: 1 }}
                >
                  <Typography
                    variant={compact ? "subtitle1" : "h6"}
                    fontWeight={600}
                  >
                    Google Health
                  </Typography>
                  <Chip
                    size="small"
                    icon={
                      isConnected ? (
                        <CheckCircleIcon sx={{ fontSize: 16 }} />
                      ) : undefined
                    }
                    label={isConnected ? "Conectado" : "Desconectado"}
                    color={isConnected ? "success" : "default"}
                    variant={isConnected ? "filled" : "outlined"}
                  />
                </Stack>
                {!compact && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {isConnected
                      ? "Seus dados de saúde já podem ser consultados no painel."
                      : "Conecte para liberar leitura dos seus dados de fitness."}
                  </Typography>
                )}
              </Box>

              {compact &&
                (isConnected ? (
                  showDisconnect ? (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={
                        isLoading ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <LinkOffIcon />
                        )
                      }
                      onClick={handleDisconnect}
                      disabled={isLoading}
                      size="small"
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 500,
                        width: { xs: "100%", sm: "auto" },
                        alignSelf: { xs: "stretch", sm: "auto" },
                      }}
                    >
                      {isLoading ? "Desvinculando..." : "Desvincular"}
                    </Button>
                  ) : null
                ) : (
                  <Button
                    variant="contained"
                    startIcon={
                      isLoading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <GoogleIcon />
                      )
                    }
                    onClick={handleLogin}
                    disabled={isLoading}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 500,
                      width: { xs: "100%", sm: "auto" },
                      alignSelf: { xs: "stretch", sm: "auto" },
                      background:
                        "linear-gradient(135deg, #4285F4 0%, #3367D6 100%)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #3367D6 0%, #2851A3 100%)",
                      },
                    }}
                  >
                    {isLoading ? "Conectando..." : "Conectar com Google Health"}
                  </Button>
                ))}
            </Stack>
          </Stack>

          {!compact && (
            <Box
              sx={{
                p: 2,
                borderRadius: 1.5,
                bgcolor: alpha("#2196F3", 0.04),
                border: "1px solid",
                borderColor: alpha("#2196F3", 0.1),
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <FavoriteIcon
                  sx={{ fontSize: 20, color: "primary.main", mt: 0.2 }}
                />
                <Box>
                  <Typography variant="body2" fontWeight={500} color="text.primary">
                    Integração com Google Health
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Permite consultar passos, calorias, distância, frequência
                    cardíaca, glicose, pressão arterial e dados de sono.
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}

          {!compact && (
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "stretch", sm: "flex-end" },
                width: "100%",
              }}
            >
              {isConnected ? (
                showDisconnect ? (
                  <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    startIcon={
                      isLoading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <LinkOffIcon />
                      )
                    }
                    onClick={handleDisconnect}
                    disabled={isLoading}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 500,
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    {isLoading ? "Desvinculando..." : "Desvincular conta"}
                  </Button>
                ) : null
              ) : (
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={
                    isLoading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <GoogleIcon />
                    )
                  }
                  onClick={handleLogin}
                  disabled={isLoading}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 500,
                    width: { xs: "100%", sm: "auto" },
                    background:
                      "linear-gradient(135deg, #4285F4 0%, #3367D6 100%)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #3367D6 0%, #2851A3 100%)",
                    },
                  }}
                >
                  {isLoading ? "Conectando..." : "Conectar com Google Health"}
                </Button>
              )}
            </Box>
          )}
        </Stack>
      </Paper>

      {showRevokeDialog && (
        <GoogleRevokeDialog
          isOpen={openRevokeModal}
          onClose={() => setOpenRevokeModal(false)}
          onOpenPermissionsPage={() =>
            window.open("https://myaccount.google.com/permissions", "_blank")
          }
        />
      )}
    </>
  );
}
